import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { assetFormFields } from '../../forms/fields/formFields';
import { assetFormSchema } from '../../forms/validationSchema/formSchema';
import { useAppDispatch } from '../../redux/hooks';
import { createAsset, updateAsset } from '../../redux/modules/asset/asset.actions';
import { assetSelector } from '../../redux/modules/asset/asset.selectors';
import { Asset } from '../../types/asset';

interface AssetDialogProps {
  open: boolean;
  initialValue: Asset | null;
  onClose: (isReload: boolean) => void;
}

export function AssetDialog({ open, initialValue, onClose }: AssetDialogProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, values, handleChange, touched, errors, submitForm, setValues, resetForm } = useFormik({
    initialValues: {
      [assetFormFields.nameEn]: '',
      [assetFormFields.nameAr]: '',
      [assetFormFields.descriptionEn]: '',
      [assetFormFields.descriptionAr]: '',
    },
    validationSchema: assetFormSchema,
    onSubmit: (formValues) => {
      if (initialValue?.id) {
        dispatch(updateAsset({ id: initialValue.id, ...formValues }));
      } else {
        dispatch(createAsset(formValues));
      }
    },
  });
  const { createStatus, updateStatus, error } = useSelector(assetSelector);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    setValues({
      nameEn: initialValue?.nameEn || '',
      nameAr: initialValue?.nameAr || '',
      descriptionEn: initialValue?.descriptionEn || '',
      descriptionAr: initialValue?.descriptionAr || '',
    });
  }, [initialValue]);

  useEffect(() => {
    if (createStatus === fetchStatuses.success) {
      onClose(true);
      enqueueSnackbar('Asset created!', { variant: 'success' });
    }
    if (createStatus === fetchStatuses.rejected) {
      enqueueSnackbar('Asset creation error!', { variant: 'error' });
    }

    if (updateStatus === fetchStatuses.success) {
      onClose(true);
      enqueueSnackbar('Asset updated!', { variant: 'success' });
    }
  }, [createStatus, updateStatus]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>{initialValue?.id ? 'Edit asset' : 'Create a new Asset'}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {error && <Alert severity="error">{error.message}</Alert>}
          Enter the asset details
        </DialogContentText>
        <form onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            id={assetFormFields.nameEn}
            name={assetFormFields.nameEn}
            label="Asset Name En"
            value={values[assetFormFields.nameEn]}
            onChange={handleChange}
            error={touched.nameEn && !!errors.nameEn}
            helperText={touched.nameEn && errors.nameEn}
            autoComplete="asset-nameEn"
          />
          <TextField
            dir="rtl"
            margin="normal"
            required
            fullWidth
            id={assetFormFields.nameAr}
            name={assetFormFields.nameAr}
            label="Asset Name Ar"
            value={values[assetFormFields.nameAr]}
            onChange={handleChange}
            error={touched.nameAr && !!errors.nameAr}
            helperText={touched.nameAr && errors.nameAr}
            autoComplete="asset-nameAr"
          />
          <TextField
            margin="normal"
            required
            fullWidth
            multiline
            id={assetFormFields.descriptionEn}
            name={assetFormFields.descriptionEn}
            label="Asset Description En"
            value={values[assetFormFields.descriptionEn]}
            onChange={handleChange}
            error={touched.descriptionEn && !!errors.descriptionEn}
            helperText={touched.descriptionEn && errors.descriptionEn}
            autoComplete="asset-descriptionEn"
          />
          <TextField
            dir="rtl"
            margin="normal"
            required
            fullWidth
            multiline
            id={assetFormFields.descriptionAr}
            name={assetFormFields.descriptionAr}
            label="Asset Description Ar"
            value={values[assetFormFields.descriptionAr]}
            onChange={handleChange}
            error={touched.descriptionAr && !!errors.descriptionAr}
            helperText={touched.descriptionAr && errors.descriptionAr}
            autoComplete="asset-descriptionAr"
          />
        </form>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={createStatus === fetchStatuses.pending}
          loadingPosition="end"
          onClick={submitForm}
        >
          {initialValue?.id ? 'Save' : 'Create'}
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={() => onClose(false)}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
